/* What the lobby screen looks like and what it says.
 *
 * The board is a tool for people who work here; the check-in screen is the only
 * part of this product a *client* ever sees. Salons care about that differently
 * — it sits in the waiting area next to their own signage — so its colours and
 * its wording are theirs to set, separately from the board's.
 *
 * Separately, but not from scratch. Every colour here falls back to the salon's
 * own theme, so a salon that sets nothing gets a lobby screen already in their
 * brand, and one that sets a single value overrides exactly that value. An
 * empty override is not a colour: it is the absence of one, and it must read as
 * "use the theme" rather than as black. That is the rule this file exists to
 * hold, and the reason it is a pure function with tests rather than a `||` chain
 * spread across a component.
 *
 * ── WHY THE COLOUR MATHS IS HERE AND NOT IMPORTED ───────────────────────────
 *
 * js/theme.js already knows how to clean a hex and how to pick text that reads
 * against a fill, and this file deliberately does not import it: theme.js
 * imports prefs.js, which reads window.localStorage as it is evaluated, so
 * importing it from a test would need a DOM to exist before the first
 * assertion. Same split, same reason, as js/rules/queue.js against sync.js and
 * js/rules/licence.js against licensing.js — the part that has to be right goes
 * where it can be asserted.
 */

/* The six a salon may set, in the order the settings screen offers them.
 *
 * `from` is the salon-theme colour each falls back to. `ready` has no `from`:
 * green means "walk in" and is not a branding decision — a salon that themes it
 * to their primary has a screen where every state looks the same. It still
 * takes an override, because a salon whose brand IS green needs to move it, but
 * its default is fixed rather than inherited.
 */
export const COLORS = [
  { key: 'primary', from: 'primary', label: 'Buttons & business name',
    hint: 'The big buttons and the wordmark at the top. Keep it dark: the text on it flips between white and near-black on its own, and a mid-tone reads poorly either way.' },
  { key: 'bg', from: 'bg', label: 'Background', hint: 'Behind everything.' },
  { key: 'surface', from: 'surface', label: 'Service tiles', hint: 'The cards on the menu, and the name box.' },
  { key: 'text', from: 'text', label: 'Headings', hint: 'The large type: the question, the answer, the receipt.' },
  { key: 'muted', from: 'muted', label: 'Smaller text', hint: 'The line under each heading, and the note on a tile.' },
  { key: 'ready', from: null, fallback: '#2C7A4B', label: 'Available now',
    hint: 'The one colour on the menu that means walk in — the tick on the receipt uses it too. Green by default and worth leaving green unless it clashes with your brand.' }
];

/* Every line of client-facing wording, and what it says out of the box.
 *
 * Grouped by the screen it appears on, because that is how somebody editing it
 * thinks about it. No placeholders and nothing interpolated: a salon typing
 * their own sentence must not be able to break the screen with a mistyped
 * token, and the one fact that genuinely varies — the closing time — is a line
 * of its own that the salon does not write.
 */
export const TEXT = [
  { key: 'ask', group: 'The menu', label: 'Heading', fallback: 'What are you here for?' },

  { key: 'nowHead', group: 'Somebody is free', label: 'Heading', fallback: 'We can see you now' },
  { key: 'nowSub', group: 'Somebody is free', label: 'Underneath', fallback: 'Take a seat and we will come and get you.' },

  { key: 'busyHead', group: 'Nobody is free', label: 'Heading',
    fallback: 'Everyone is with a client',
    hint: 'Only shown when wait times are switched off. With them on, the wait itself is the heading.' },
  { key: 'noneHead', group: 'Nobody is free', label: 'Heading when nobody does it today',
    fallback: 'We cannot do that today' },
  { key: 'joinSub', group: 'Nobody is free', label: 'Underneath',
    fallback: 'Leave your name and we will come and get you when it is your turn.' },
  { key: 'joinButton', group: 'Nobody is free', label: 'Button', fallback: 'Join the waiting list' },

  /* The wait was past the salon's cutoff and there is a booking link to send
   * them to — see js/rules/booking.js. Nothing here names a length of time on
   * purpose: the cutoff is a number in a settings box, and a salon that moves it
   * from an hour to twenty minutes must not be left with a sentence on a lobby
   * screen still saying "over an hour". */
  { key: 'bookHead', group: 'Too long to wait', label: 'Heading',
    fallback: 'It is a long wait today',
    hint: 'Shown instead of the waiting list when the wait is past the cutoff under Booking instead of waiting.' },
  { key: 'bookSub', group: 'Too long to wait', label: 'Underneath',
    fallback: 'You are welcome to book a time that suits you instead.' },
  { key: 'bookButton', group: 'Too long to wait', label: 'Button',
    fallback: 'Book an appointment' },

  { key: 'deskHead', group: 'Send them to the desk', label: 'Heading', fallback: 'Please see the front desk' },
  { key: 'deskSub', group: 'Send them to the desk', label: 'Underneath',
    fallback: 'There is not enough time for that today.' },

  { key: 'seatedHead', group: 'Checked in', label: 'Heading', fallback: 'You are checked in' },
  { key: 'seatedSub', group: 'Checked in', label: 'Underneath', fallback: 'Take a seat — we will come and get you.' },
  { key: 'checkButton', group: 'Checked in', label: 'Button', fallback: 'Check in' },

  { key: 'listHead', group: 'On the waiting list', label: 'Heading', fallback: 'You are on the list' },
  { key: 'listSub', group: 'On the waiting list', label: 'Underneath', fallback: 'Please take a seat. We will come and get you.' },

  { key: 'closedHead', group: 'Closed', label: 'Heading', fallback: 'We are closed' },
  { key: 'closedSub', group: 'Closed', label: 'Underneath', fallback: 'Please see the front desk.' }
];

// The longest a salon may make one of these. Not a database limit — it is the
// point past which a sentence stops fitting on the screen it is drawn on, and
// a heading that wraps to four lines on a tablet is worse than one that was
// refused at the box.
export const TEXT_MAX = 120;

/* #rrggbb, or null for anything that is not a colour.
 *
 * Null is load-bearing: it is what makes an empty box mean "use the theme"
 * rather than "use black". A half-typed hex on its way to being valid is also
 * null, so a colour never flickers to something wrong mid-keystroke.
 */
export function cleanColor(value) {
  if (typeof value !== 'string') return null;
  var v = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(v)) v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
  return /^[0-9a-f]{6}$/i.test(v) ? '#' + v.toLowerCase() : null;
}

// One line of wording, as it will be drawn. Whitespace collapses because a
// paste out of a document brings newlines with it, and an empty box is not a
// blank heading — it is "I have not set one", which is the fallback.
export function cleanText(value) {
  if (value == null) return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, TEXT_MAX);
}

/* Perceived brightness, 0–1, for choosing what reads on top of a fill.
 *
 * The sRGB coefficients rather than a plain average: the eye is roughly six
 * times more sensitive to green than to blue, so an average calls a saturated
 * blue "mid" and puts grey text on it.
 */
function luminance(hex) {
  var v = cleanColor(hex) || '#000000';
  var r = parseInt(v.slice(1, 3), 16) / 255;
  var g = parseInt(v.slice(3, 5), 16) / 255;
  var b = parseInt(v.slice(5, 7), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/* White or near-black, whichever reads on that fill.
 *
 * Derived rather than stored, and that is the whole reason this is not just six
 * strings on a settings screen. A salon picks a button colour; nobody picks the
 * colour of the text on the button, and a salon that picked a pale primary
 * would otherwise get white-on-white and a screen with no visible buttons at
 * all — on the one screen in this product with no member of staff standing in
 * front of it to notice.
 *
 * Near-black rather than pure: #14181E against a pale fill is the same
 * judgement js/theme.js makes for the top bar, and matching it means the lobby
 * and the board treat a pale brand the same way.
 */
export function readableOn(hex) {
  return luminance(hex) > 0.55 ? '#14181E' : '#FFFFFF';
}

/* What the screen actually draws with.
 *
 * `stored` is whatever is in the salon's settings — `{ colors, text }`, either
 * possibly absent, holding possibly-rubbish values. `themeColors` is the
 * salon's resolved board theme, which every colour without an override falls
 * back to.
 *
 * Total by construction: every key in COLORS and TEXT comes back with a usable
 * value whatever it was handed, including nothing at all. The lobby screen is
 * the one screen in this product that can be left running unattended in front
 * of customers, so "renders wrong" and "renders blank" are the same failure and
 * neither is allowed to depend on what somebody typed into a settings box.
 */
export function resolveLook(stored, themeColors) {
  var storedColors = (stored && stored.colors) || {};
  var storedText = (stored && stored.text) || {};
  var theme = themeColors || {};

  var colors = {};
  COLORS.forEach(function (f) {
    colors[f.key] = cleanColor(storedColors[f.key]) ||
      (f.from ? cleanColor(theme[f.from]) : null) ||
      f.fallback || '#000000';
  });
  // Not settable, and not stored: see `readableOn`.
  colors.onPrimary = readableOn(colors.primary);

  var text = {};
  TEXT.forEach(function (f) {
    text[f.key] = cleanText(storedText[f.key]) || f.fallback;
  });

  return { colors: colors, text: text };
}

/* The resolved colours as the custom properties the stylesheet reads.
 *
 * Prefixed `--ci-` and never sharing a name with the board's, so the two
 * stylesheets can be loaded on the same document — which they are, because
 * checkin.html takes dash.css for the reset — without either being able to
 * repaint the other.
 */
export function cssVars(look) {
  var c = (look && look.colors) || {};
  return {
    '--ci-bg': c.bg,
    '--ci-surface': c.surface,
    '--ci-primary': c.primary,
    '--ci-on-primary': c.onPrimary,
    '--ci-text': c.text,
    '--ci-muted': c.muted,
    '--ci-ready': c.ready
  };
}

// Whether the salon has set anything at all — what the settings screen's
// "Reset" offers itself on, and what tells "same as the board" from "chosen and
// happens to match".
export function isCustomised(stored) {
  var c = (stored && stored.colors) || {};
  var t = (stored && stored.text) || {};
  return COLORS.some(function (f) { return !!cleanColor(c[f.key]); }) ||
    TEXT.some(function (f) { return !!cleanText(t[f.key]); });
}
